import React from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import {
  ReelsResponse,
  ReelsSaveArgument,
  ReelsSaveResponse
} from '../types/responses/reels-response';
import { UploadSingleFileAsync } from '../utils/storage';
import {
  apiMutation,
  apiFetcherWithoutThrowError,
  apiMutationStream
} from '../utils/fetchers';

export const useGetReels = (gameCode: string) => {
  const { data, error, isLoading } = useSWR<
    ReelsResponse & { message: string; status: number }
  >(`games/${gameCode}/reels-projects`, apiFetcherWithoutThrowError, {
    suspense: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return { data, error, isLoading };
};

const fetchResourceFiles = (
  url: string,
  {
    arg: { files, gameCode }
  }: {
    arg: { gameCode: string; files: File[] };
  }
) => {
  return Promise.all(
    files.map((file) => UploadSingleFileAsync(gameCode ?? '', file))
  );
};

export const useUploadReelsResources = (gameCode: string) => {
  const { data, error, isMutating, trigger } = useSWRMutation(
    `games/${gameCode}/players/upload`,
    fetchResourceFiles
  );

  return { data, error, isMutating, trigger };
};

export const useSaveReels = (gameCode: string) => {
  const { data, error, isMutating, trigger } = useSWRMutation<
    ReelsSaveResponse,
    Error,
    string,
    ReelsSaveArgument
  >(`games/${gameCode}/reels-projects`, apiMutation, {
    revalidate: false
  });

  return { data, error, isMutating, trigger };
};

export const usePreviewReels = (onComplete: (videoUrl: string) => void) => {
  const controllerRef = React.useRef<AbortController | null>(null);

  const [progress, setProgress] = React.useState<number>(0);
  const [status, setStatus] = React.useState<string>('');
  const [data, setData] = React.useState<string>('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const trigger = async (arg: any) => {
    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    setData('');
    setError(null);

    try {
      await apiMutationStream(
        'https://highlight-reel-server-cswng.ondigitalocean.app/create-highlight-reel',
        arg,
        controller.signal,
        ({ event, data }) => {
          switch (event) {
            case 'queued':
            case 'waiting':
            case 'started':
            case 'status':
              setStatus(data);
              break;
            case 'progress':
              setProgress(parseInt(data));
              break;
            case 'complete':
              setStatus('Preview rendered completely');
              const result = JSON.parse(data) as { url: string };
              setData(result.url);
              break;
            case 'error':
              const message = `${data}.\nPlease try again.`;
              setStatus('');
              setError({
                name: '',
                message
              });
              setLoading(false);
              break;
          }
        },
        (videoUrl) => {
          setProgress(100);
          setLoading(false);
          onComplete(videoUrl);
        },
        () => {
          setLoading(false);
        }
      );
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setLoading(false);
        setError(err);
      }
    }
  };

  const cancel = () => {
    controllerRef.current?.abort();
  };

  return { status, progress, loading, data, error, trigger, cancel };
};

export const useSaveReelsVideo = (gameCode: string) => {
  const { data, error, isMutating, trigger } = useSWRMutation<
    any,
    Error,
    string,
    { s3PresignedUrl: string }
  >(`games/${gameCode}/reels-projects/video`, apiMutation, {
    revalidate: false
  });

  return { data, error, isMutating, trigger };
};

